import React, { forwardRef } from "react";
import classNames from "classnames";
import { useTheme } from "App/common/ThemeProvider";
import { LoadingSpinner } from "App/common/icons/LoadingSpinner";
import "./Button.scss";

export enum BUTTON_THEME {
  PLAIN = "plain",
  TEXT = "text",
  LINK = "link",
  ICON = "icon",
  PRIMARY = "primary",
  PRIMARY_INVERSE = "primary-inverse",
  SECONDARY = "secondary",
  CTA = "cta",
  FLAT_BLACK = "flat-black",
  WARN = "warn",
  MONETIZATION = "monetization",
  MONETIZATION_INVERSE = "monetization-inverse",
}

export enum BUTTON_SIZE {
  XLARGE = "xlarge",
  LARGE = "large",
  MEDIUM = "medium",
  SMALL = "small",
  XSMALL = "xsmall",
  XXSMALL = "xxsmall",
  AUTO = "auto",
}

export type ButtonProps = Omit<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >,
  "onClick"
> & {
  /**
   * Widened from the button-only handler so the anchor variant (`href`) can
   * forward it too. On the anchor it's a side-effect hook only: Button never
   * calls preventDefault there, so native navigation always proceeds.
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  theme?: BUTTON_THEME;
  size?: BUTTON_SIZE;
  loading?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  /**
   * When set, renders a real `<a href>` (styled identically) instead of a
   * `<button>`. Use for navigation CTAs: in-app browsers (e.g. Instagram's)
   * swallow JS-initiated navigation from onClick, but handle genuine anchor
   * taps natively. The anchor variant forwards `onClick` for side effects
   * (never preventDefault, so navigation always proceeds) and doesn't support
   * `disabled`; it takes the shared visual props plus `target`/`rel`, and
   * button-only DOM props and `ref` are not forwarded to it.
   */
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props: ButtonProps, ref) => {
    /* eslint-disable react/prop-types */
    const {
      theme: buttonTheme,
      size,
      loading,
      startIcon,
      endIcon,
      fullWidth,
      href,
      target,
      rel,
      ...rest
    } = props;
    const { theme } = useTheme();

    const isDisabled = props.disabled || loading;
    const className = classNames(
      "highnote-button",
      { "full-width": fullWidth },
      props.className,
    );
    const style: React.CSSProperties = {
      pointerEvents: "auto",
      ...(props.style || {}),
    };
    const sharedDataAttributes = {
      "data-theme": theme,
      "data-button-theme": buttonTheme || BUTTON_THEME.PRIMARY,
      "data-size": size || BUTTON_SIZE.MEDIUM,
      "data-disabled": isDisabled,
    };

    const content = (
      <>
        {startIcon && <span className="start-icon">{startIcon}</span>}
        {props.children}
        {!!loading && <LoadingSpinner />}
        {endIcon && <span className="end-icon">{endIcon}</span>}
      </>
    );

    if (href) {
      return (
        <a
          {...sharedDataAttributes}
          href={href}
          target={target}
          rel={rel}
          className={className}
          style={style}
          onClick={props.onClick}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        {...rest}
        {...sharedDataAttributes}
        ref={ref}
        className={className}
        style={style}
        disabled={isDisabled}
        onClick={(e) => {
          if (props.disabled) {
            e.preventDefault();
            return;
          }

          props.onClick && props.onClick(e);
        }}
      >
        {content}
      </button>
    );
  },
);
Button.displayName = "Button";
